import React, { useEffect, useState } from "react";
import {
  Typography,
  Divider,
  Box,
  Grid,
  Paper,
  CircularProgress,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Chip,
  Fade,
  Skeleton,
} from "@mui/material";
import { Bar, Line, Pie, Doughnut, PolarArea } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip as ChartTooltip,
  Legend,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  RadialLinearScale,
} from "chart.js";
import {
  Groups,
  Store,
  Business,
  LocationOn,
  TrendingUp,
  AttachMoney,
  Receipt,
  Info,
  TrendingDown,
  Person,
  Refresh,
  NavigateBefore,
  NavigateNext,
  ShowChart,
  TableView,
  Download,
  Fullscreen,
} from "@mui/icons-material";
import httpRequest from "../utils/httpRequest";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  ChartTooltip,
  Legend,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  RadialLinearScale
);

const metricCards = [
  { 
    title: "Total Customers", 
    key: "totalCustomers", 
    icon: <Groups fontSize="large" />,
    color: "#42a5f5"
  },
  { 
    title: "Total Suppliers", 
    key: "totalSuppliers", 
    icon: <Store fontSize="large" />,
    color: "#66bb6a"
  },
  { 
    title: "Total Firms", 
    key: "totalFirms", 
    icon: <Business fontSize="large" />,
    color: "#ffa726"
  },
  { 
    title: "Service Locations", 
    key: "totalServiceLocations", 
    icon: <LocationOn fontSize="large" />,
    color: "#ef5350"
  },
];

const Analytics = () => {
  const [data, setData] = useState(null);
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState("");
  const [selectedInvoiceType, setSelectedInvoiceType] = useState("");
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");
  const [todaysRecoveries, setTodaysRecoveries] = useState([]);
  const [todaysPage, setTodaysPage] = useState(1);
  const [todaysRecoveriesLoading, setTodaysRecoveriesLoading] = useState(true);
  const [recoveriesDate, setRecoveriesDate] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });
  const [recoveriesRefreshing, setRecoveriesRefreshing] = useState(false);
  const [showRecoveriesGraph, setShowRecoveriesGraph] = useState(true);
  const [recoveriesSeries, setRecoveriesSeries] = useState([]);
  const [recoveriesEndDate, setRecoveriesEndDate] = useState(() => new Date().toISOString().slice(0,10));
  const [recoveriesStartDate, setRecoveriesStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().slice(0,10);
  });
  const [recentInvoicesDate, setRecentInvoicesDate] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });
  const [filteredRecentInvoices, setFilteredRecentInvoices] = useState([]);
  const [recentInvoicesLoading, setRecentInvoicesLoading] = useState(false);
  const [recentInvoicesPage, setRecentInvoicesPage] = useState(1);
  const [chartsLoading, setChartsLoading] = useState(true);
  const theme = useTheme();

  const getCurrentFinancialYear = (financialYears) => {
    const currentDate = new Date();
    const currentFY = financialYears.find(fy => {
      const startDate = new Date(fy.start_date);
      const endDate = new Date(fy.end_date);
      return currentDate >= startDate && currentDate <= endDate;
    });
    return currentFY?.id || "";
  };

  useEffect(() => {
    const fetchFirms = async () => {
      try {
        const res = await httpRequest("firm/getAll", "GET");
        setFirms(res || []);
      } catch (err) {
        console.error("Failed to fetch firms", err);
      }
    };

    const fetchFinancialYears = async () => {
      try {
        const res = await httpRequest("invoice/getAllFinancialYears", "GET");
        setFinancialYears(res || []);
        // Set current financial year as default
        const currentFYId = getCurrentFinancialYear(res);
        if (currentFYId) {
          setSelectedFY(currentFYId);
        }
      } catch (err) {
        console.error("Failed to fetch financial years", err);
      }
    };

    fetchFirms();
    fetchFinancialYears();
  }, []);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setChartsLoading(true);
      try {
        const queryParams = new URLSearchParams();
        if (selectedFirm) queryParams.append("firm_id", selectedFirm);
        if (selectedInvoiceType) queryParams.append("invoice_type", selectedInvoiceType);
        if (selectedFY) queryParams.append("financial_year_id", selectedFY);
        
        const res = await httpRequest(`analytics?${queryParams.toString()}`, "GET");
        setData(res);
      } catch (err) {
        console.error("Failed to fetch analytics", err);
      } finally {
        setChartsLoading(false);
      }
    };

    fetchAnalytics();
  }, [selectedFirm, selectedInvoiceType, selectedFY]);

  const fetchTodaysRecoveries = async (date = recoveriesDate) => {
    setTodaysRecoveriesLoading(true);
    setRecoveriesRefreshing(true);
    try {
      const url = date
        ? `analytics/todays-recoveries?date=${date}`
        : `analytics/todays-recoveries`;
      const res = await httpRequest(url);
      setTodaysRecoveries(res || []);
      setTodaysRecoveriesLoading(false);
      setRecoveriesRefreshing(false);
    } catch (err) {
      setTodaysRecoveries([]);
      setTodaysRecoveriesLoading(false);
      setRecoveriesRefreshing(false);
    }
  };

  const fetchRecoveriesLastDays = async (days = 15, endDate = recoveriesEndDate) => {
    setRecoveriesRefreshing(true);
    try {
      const params = new URLSearchParams();
      params.set('days', String(days));
      if (endDate) params.set('end_date', endDate);
      const res = await httpRequest(`analytics/recoveries-last-days?${params.toString()}`, 'GET');
      setRecoveriesSeries(res?.data || []);
      if (res?.end_date) setRecoveriesEndDate(res.end_date);
      if (res?.start_date) setRecoveriesStartDate(res.start_date);
    } catch (err) {
      setRecoveriesSeries([]);
    } finally {
      setRecoveriesRefreshing(false);
    }
  };

  // Load recoveries data on initial load and when mode toggles
  useEffect(() => {
    if (showRecoveriesGraph) {
      fetchRecoveriesLastDays(15);
    } else {
      fetchTodaysRecoveries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRecoveriesGraph]);

  const fetchFilteredRecentInvoices = async (date) => {
    if (!date) {
      setFilteredRecentInvoices([]);
      return;
    }
    
    setRecentInvoicesLoading(true);
    try {
      const url = `analytics/todays-invoices?date=${date}`;
      const res = await httpRequest(url);
      setFilteredRecentInvoices(res || []);
    } catch (err) {
      console.error("Failed to fetch filtered invoices", err);
      setFilteredRecentInvoices([]);
    } finally {
      setRecentInvoicesLoading(false);
    }
  };

  useEffect(() => {
    fetchTodaysRecoveries();
  }, [recoveriesDate]);

  useEffect(() => {
    fetchFilteredRecentInvoices(recentInvoicesDate);
  }, []);

  const handleDateChange = (newDate) => {
    setRecoveriesDate(newDate);
    setTodaysPage(1);
  };

  const handleRefresh = () => {
    if (showRecoveriesGraph) {
      fetchRecoveriesLastDays(15);
    } else {
      fetchTodaysRecoveries();
    }
  };

  const handleRecentInvoicesDateChange = (newDate) => {
    setRecentInvoicesDate(newDate);
    setRecentInvoicesPage(1); // Reset to first page when date changes
    fetchFilteredRecentInvoices(newDate);
  };

  const navigateDate = (direction) => {
    const currentDate = new Date(recoveriesDate);
    if (direction === 'prev') {
      currentDate.setDate(currentDate.getDate() - 1);
    } else {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    const today = new Date();
    if (currentDate > today) return;
    
    const newDate = currentDate.toISOString().slice(0, 10);
    handleDateChange(newDate);
  };

  const renderTodaysRecoveries = () => {
    const pageSize = 5;
    const totalPages = Math.ceil(todaysRecoveries.length / pageSize);
    const paginated = todaysRecoveries.slice((todaysPage - 1) * pageSize, todaysPage * pageSize);
  
    if (todaysRecoveriesLoading) {
      return <CircularProgress />;
    }
  
    if (!todaysRecoveries || todaysRecoveries.length === 0) {
      return <Typography>No customers paid today.</Typography>;
    }
  
    return (
      <>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Customer Name</TableCell>
                <TableCell>Account Number</TableCell>
                <TableCell align="right">Amount Paid</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((customer) => (
                <TableRow key={customer.account_number}>
                  <TableCell>{customer.name}</TableCell>
                  <TableCell>{customer.account_number}</TableCell>
                  <TableCell align="right">₹{parseFloat(customer.total_amount_paid).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={totalPages}
              page={todaysPage}
              onChange={(event, value) => setTodaysPage(value)}
              color="primary"
              size="small"
              shape="rounded"
            />
          </Box>
        )}
      </>
    );
  };  

  if (!data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  const months = data.monthlyInvoiceStats.map((item) => item.month);
  const invoiceCounts = data.monthlyInvoiceStats.map((item) => parseInt(item.invoice_count));
  const totalSales = data.monthlyInvoiceStats.map((item) => parseFloat(item.total_sales));

  const createChartOptions = (type = 'bar') => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: type !== 'pie' && type !== 'doughnut' && type !== 'polarArea',
        position: 'top',
        labels: {
          color: theme.palette.text.primary,
          font: {
            size: 12,
            weight: '500',
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.95)',
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        padding: 12,
        titleFont: {
          size: 14,
          weight: '600',
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const parsed = context.parsed;
            const value = (parsed && typeof parsed === 'object') ? (parsed.y ?? 0) : (parsed ?? 0);
            // Always show as integer for invoice counts
            if (label.includes('Invoice') || label.includes('invoice')) {
              return `${label}: ${parseInt(Number(value), 10)}`;
            }
            if (label.includes('Sales') || label.includes('Revenue') || label.includes('Amount')) {
              return `${label}: ₹${parseFloat(String(value)).toLocaleString()}`;
            }
            return `${label}: ${value}`;
          },
        },
      },
    },
    scales: type !== 'pie' && type !== 'doughnut' && type !== 'polarArea' ? {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            size: 11,
          },
          maxRotation: 45,
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: {
          color: theme.palette.divider,
          drawBorder: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            size: 11,
          },
          callback: function(value) {
            return Number.isInteger(value) ? value : '';
          },
        },
        border: {
          display: false,
        },
      },
    } : undefined,
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6,
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.background.paper,
        borderWidth: 2,
      },
      line: {
        tension: 0.4,
        borderWidth: 3,
      },
    },
  });

  const createGradient = (ctx, color1, color2) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, color1);
    gradient.addColorStop(1, color2);
    return gradient;
  };

  const renderRecoveriesGraph = () => {
    if (recoveriesRefreshing && recoveriesSeries.length === 0) {
      return <CircularProgress />;
    }
    if (!recoveriesSeries.length) {
      return <Typography>No data for the selected period.</Typography>;
    }

    const labels = recoveriesSeries.map(p => p.date.slice(5));
    const values = recoveriesSeries.map(p => parseFloat(p.amount_received || 0));
    const customers = recoveriesSeries.map(p => parseInt(p.customers_paid || 0, 10));

    const data = {
      labels,
      datasets: [
        {
          label: 'Amount Received (₹)',
          data: values,
          borderColor: theme.palette.primary.main,
          backgroundColor: theme.palette.primary.light + '55',
          fill: true,
          tension: 0.35,
          borderWidth: 2,
          pointRadius: 3,
          yAxisID: 'y',
        },
        {
          label: 'Customers Paid',
          data: customers,
          borderColor: theme.palette.success.main,
          backgroundColor: theme.palette.success.light + '55',
          fill: false,
          tension: 0.25,
          borderWidth: 2,
          pointRadius: 3,
          yAxisID: 'y1',
        }
      ],
    };

    return (
      <Box>
        <Box height={260}>
          <Line 
            data={data} 
            options={{
              ...createChartOptions('line'),
              scales: {
                y: {
                  position: 'left',
                  ticks: {
                    color: theme.palette.text.secondary,
                    callback: (value) => `₹${Number(value).toLocaleString()}`
                  },
                  grid: { color: theme.palette.divider, drawBorder: false },
                },
                y1: {
                  position: 'right',
                  ticks: {
                    color: theme.palette.text.secondary,
                    precision: 0,
                  },
                  grid: { display: false },
                },
                x: {
                  grid: { display: false },
                  ticks: { color: theme.palette.text.secondary }
                }
              },
              plugins: {
                ...createChartOptions('line').plugins,
                tooltip: {
                  ...createChartOptions('line').plugins.tooltip,
                  callbacks: {
                    label: function(context) {
                      const label = context.dataset.label || '';
                      const parsed = context.parsed;
                      const value = (parsed && typeof parsed === 'object') ? (parsed.y ?? 0) : (parsed ?? 0);
                      if (label.includes('Amount')) {
                        return `${label}: ₹${Number(value).toLocaleString()}`;
                      }
                      return `${label}: ${Number(value).toLocaleString()}`;
                    },
                  },
                },
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  const invoiceCountData = {
    labels: months,
    datasets: [
      {
        label: "Number of Invoices",
        data: invoiceCounts,
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.dark,
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
        hoverBackgroundColor: theme.palette.primary.dark,
        hoverBorderColor: theme.palette.primary.main,
      },
    ],
  };

  const salesAreaData = {
    labels: months,
    datasets: [
      {
        label: "Total Sales (₹)",
        data: totalSales,
        borderColor: theme.palette.primary.main,
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return theme.palette.primary.main;
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, theme.palette.primary.main + '80');
          gradient.addColorStop(0.5, theme.palette.primary.light + '40');
          gradient.addColorStop(1, theme.palette.primary.light + '10');
          return gradient;
        },
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: theme.palette.primary.main,
        pointBorderColor: theme.palette.background.paper,
        pointBorderWidth: 3,
        pointRadius: 6,
        pointHoverRadius: 10,
        pointHoverBackgroundColor: theme.palette.primary.dark,
        pointHoverBorderColor: theme.palette.primary.main,
        yAxisID: 'y',
      },
      {
        label: "Invoice Count",
        data: invoiceCounts,
        borderColor: theme.palette.success.main,
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return theme.palette.success.main;
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, theme.palette.success.main + '60');
          gradient.addColorStop(0.5, theme.palette.success.light + '30');
          gradient.addColorStop(1, theme.palette.success.light + '05');
          return gradient;
        },
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: theme.palette.success.main,
        pointBorderColor: theme.palette.background.paper,
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 8,
        pointHoverBackgroundColor: theme.palette.success.dark,
        pointHoverBorderColor: theme.palette.success.main,
        yAxisID: 'y1',
      },
    ],
  };

  const invoiceTypeData = {
    labels: data.invoiceTypeDistribution.map(item => 
      item.type === 'gst' ? 'GST Invoices' : 'Kachcha Invoices'
    ),
    datasets: [
      {
        data: data.invoiceTypeDistribution.map(item => parseInt(item.count, 10)),
        backgroundColor: [
          theme.palette.success.main,
          theme.palette.warning.main,
          theme.palette.info.main,
        ],
        borderColor: [
          theme.palette.success.dark,
          theme.palette.warning.dark,
          theme.palette.info.dark,
        ],
        borderWidth: 2,
        hoverOffset: 4,
      },
    ],
  };

  const invoiceTypePolarData = {
    labels: data.invoiceTypeDistribution.map(item => 
      item.type === 'gst' ? 'GST Invoices' : 'Kachcha Invoices'
    ),
    datasets: [
      {
        data: data.invoiceTypeDistribution.map(item => parseInt(item.count, 10)),
        backgroundColor: [
          theme.palette.success.main + '80',
          theme.palette.warning.main + '80',
          theme.palette.info.main + '80',
        ],
        borderColor: [
          theme.palette.success.main,
          theme.palette.warning.main,
          theme.palette.info.main,
        ],
        borderWidth: 2,
        hoverBackgroundColor: [
          theme.palette.success.main,
          theme.palette.warning.main,
          theme.palette.info.main,
        ],
        hoverBorderColor: [
          theme.palette.success.dark,
          theme.palette.warning.dark,
          theme.palette.info.dark,
        ],
      },
    ],
  };

  // Process monthly invoice type stats for chart
  const processMonthlyInvoiceTypeData = () => {
    if (!data?.monthlyInvoiceTypeStats || data.monthlyInvoiceTypeStats.length === 0) {
      return {
        labels: [],
        gstCounts: [],
        kachchaCounts: []
      };
    }

    // Get all unique months
    const allMonths = [...new Set(data.monthlyInvoiceTypeStats.map(item => item.month))].sort();
    
    // Create maps for GST and Kachcha counts by month
    const gstMap = new Map();
    const kachchaMap = new Map();
    
    data.monthlyInvoiceTypeStats.forEach((item) => {
      const count = parseInt(item.invoice_count) || 0;
      if (item.type === 'gst') {
        gstMap.set(item.month, count);
      } else if (item.type === 'kachcha') {
        kachchaMap.set(item.month, count);
      }
    });

    // Build arrays for each month
    const gstCounts = allMonths.map(month => gstMap.get(month) || 0);
    const kachchaCounts = allMonths.map(month => kachchaMap.get(month) || 0);

    return {
      labels: allMonths,
      gstCounts,
      kachchaCounts
    };
  };

  const monthlyInvoiceTypeChartData = processMonthlyInvoiceTypeData();

  const monthlyInvoiceTypeBarData = {
    labels: monthlyInvoiceTypeChartData.labels,
    datasets: [
      {
        label: "GST Invoices",
        data: monthlyInvoiceTypeChartData.gstCounts,
        backgroundColor: theme.palette.success.main,
        borderColor: theme.palette.success.dark,
        borderWidth: 2,
        borderRadius: 6,
        borderSkipped: false,
        hoverBackgroundColor: theme.palette.success.dark,
        hoverBorderColor: theme.palette.success.main,
      },
      {
        label: "Kachcha Invoices",
        data: monthlyInvoiceTypeChartData.kachchaCounts,
        backgroundColor: theme.palette.warning.main,
        borderColor: theme.palette.warning.dark,
        borderWidth: 2,
        borderRadius: 6,
        borderSkipped: false,
        hoverBackgroundColor: theme.palette.warning.dark,
        hoverBorderColor: theme.palette.warning.main,
      },
    ],
  };

  const StatCard = ({ title, value, icon, color, tooltip, subtext }) => (
    <Card 
      elevation={4}
      sx={{
        height: '100%',
        borderRadius: 3,
        transition: "0.3s",
        ":hover": {
          boxShadow: 6,
          transform: "translateY(-4px)",
        },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ color: color }}>{icon}</Box>
            <Typography variant="h6" color="text.secondary">
              {title}
            </Typography>
          </Box>
          {tooltip && (
            <Tooltip title={tooltip}>
              <IconButton size="small">
                <Info fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography variant="h4" fontWeight="bold" color={color}>
          {value}
        </Typography>
        {subtext && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            {subtext}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

    const renderInvoiceTypeDistribution = () => {
    if (!data?.invoiceTypeDistribution?.length) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">No invoice type data available</Typography>
        </Box>
      );
    }

    return (
      <Fade in={true} timeout={800}>
        <Box sx={{ height: 300, position: 'relative' }}>
          <PolarArea
            data={invoiceTypePolarData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: {
                    color: theme.palette.text.primary,
                    font: {
                      size: 12,
                      weight: '500',
                    },
                    padding: 15,
                    usePointStyle: true,
                    pointStyle: 'circle',
                  },
                },
                tooltip: {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.95)',
                  titleColor: theme.palette.text.primary,
                  bodyColor: theme.palette.text.secondary,
                  borderColor: theme.palette.divider,
                  borderWidth: 1,
                  cornerRadius: 8,
                  displayColors: true,
                  padding: 12,
                  titleFont: {
                    size: 14,
                    weight: '600',
                  },
                  bodyFont: {
                    size: 13,
                  },
                  callbacks: {
                    label: function(context) {
                      const label = context.label || '';
                      const value = context.raw || 0;
                      const total = context.dataset.data.reduce((a, b) => a + b, 0);
                      const percentage = ((value / total) * 100).toFixed(1);
                      return `${label}: ${parseInt(value, 10)} (${percentage}%)`;
                    },
                  },
                },
              },
              scales: {
                r: {
                  grid: {
                    color: theme.palette.divider,
                  },
                  ticks: {
                    color: theme.palette.text.secondary,
                    font: {
                      size: 11,
                    },
                    backdropColor: 'transparent',
                    callback: function(value) {
                      return Number.isInteger(value) ? value : '';
                    },
                  },
                  pointLabels: {
                    color: theme.palette.text.primary,
                    font: {
                      size: 12,
                      weight: '500',
                    },
                  },
                },
              },
            }}
          />
        </Box>
      </Fade>
    );
  };

  const renderTopCustomers = () => {
    if (!data?.topCustomers?.length) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">No customer data available</Typography>
        </Box>
      );
    }

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell align="right">Revenue</TableCell>
              <TableCell align="right">Invoices</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.topCustomers.map((customer, index) => (
              <TableRow key={index}>
                <TableCell>{customer.customer_name}</TableCell>
                <TableCell align="right">
                  ₹{parseFloat(customer.total_revenue).toLocaleString()}
                </TableCell>
                <TableCell align="right">{customer.invoice_count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderRecentInvoices = () => {
    const invoicesToShow = recentInvoicesDate ? filteredRecentInvoices : data?.recentInvoices;
    
    if (recentInvoicesLoading) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    
    if (!invoicesToShow?.length) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {recentInvoicesDate ? 'No invoices found for selected date' : 'No recent invoices available'}
          </Typography>
        </Box>
      );
    }

    // Apply pagination only for filtered results
    const pageSize = 5;
    const totalPages = Math.ceil(invoicesToShow.length / pageSize);
    const paginated = recentInvoicesDate 
      ? invoicesToShow.slice((recentInvoicesPage - 1) * pageSize, recentInvoicesPage * pageSize)
      : invoicesToShow;
    
    return (
      <>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((inv, idx) => (
                <TableRow key={inv.id || inv.invoice_number || idx}>
                  <TableCell>{inv.invoice_number}</TableCell>
                  <TableCell>{inv.Customer?.name || inv.name || '-'}</TableCell>
                  <TableCell>
                    {inv.type && (
                      <Chip 
                        label={inv.is_cn ? 'Credit Note' : (inv.type === 'gst' ? 'GST' : 'Kachcha')} 
                        size="small"
                        color={inv.is_cn ? 'error' : (inv.type === 'gst' ? 'success' : 'warning')}
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">₹{parseFloat(inv.grand_total).toLocaleString()}</TableCell>
                  <TableCell>
                    {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {recentInvoicesDate && totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={totalPages}
              page={recentInvoicesPage}
              onChange={(event, value) => setRecentInvoicesPage(value)}
              color="primary"
              size="small"
              shape="rounded"
            />
          </Box>
        )}
      </>
    );
  };

  const ChartContainer = ({ title, children, subtitle, actions, loading = false }) => (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 3, 
        borderRadius: 3, 
        height: '100%',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(145deg, #1a1a1a 0%, #2d2d2d 100%)'
          : 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
        border: `1px solid ${theme.palette.divider}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: theme.shadows[8],
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={600} color="text.primary" display="flex" alignItems="center" gap={1}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Box display="flex" gap={1}>
            {actions}
          </Box>
        )}
      </Box>
      <Fade in={!loading} timeout={600}>
        <Box>
          {loading ? (
            <Box height={300} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            children
          )}
        </Box>
      </Fade>
    </Paper>
  );

  return (
    <Box p={{ xs: 2, md: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4">
          Analytics Dashboard
        </Typography>
        <Box display="flex" gap={2}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="firm-select-label">Firm</InputLabel>
            <Select
              labelId="firm-select-label"
              value={selectedFirm}
              label="Firm"
              onChange={(e) => setSelectedFirm(e.target.value)}
            >
              <MenuItem value="">All Firms</MenuItem>
              {firms.map((firm) => (
                <MenuItem key={firm.id} value={firm.id}>
                  {firm.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="invoice-type-select-label">Invoice Type</InputLabel>
            <Select
              labelId="invoice-type-select-label"
              value={selectedInvoiceType}
              label="Invoice Type"
              onChange={(e) => setSelectedInvoiceType(e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="gst">GST</MenuItem>
              <MenuItem value="kachcha">Kachcha</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="fy-select-label">Financial Year</InputLabel>
            <Select
              labelId="fy-select-label"
              value={selectedFY}
              label="Financial Year"
              onChange={(e) => setSelectedFY(e.target.value)}
            >
              <MenuItem value="">All Years</MenuItem>
              {financialYears.map((fy) => (
                <MenuItem key={fy.id} value={fy.id}>
                  {fy.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Grid container spacing={3} mb={4}>
        {metricCards.map((metric) => (
          <Grid
            key={metric.key}
            item
            size={{ xs: 12, md: 3, sm: 3 }}
          >
            <Paper
              elevation={4}
              sx={{
                p: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderRadius: 3,
                transition: "0.3s",
                ":hover": {
                  boxShadow: 6,
                  transform: "translateY(-4px)",
                },
                bgcolor: `${metric.color}15`,
              }}
            >
              <Box sx={{ color: metric.color }}>{metric.icon}</Box>
              <Box textAlign="right">
                <Typography variant="subtitle2" color="text.secondary">
                  {metric.title}
                </Typography>
                <Typography variant="h6" fontWeight={600} color={metric.color}>
                  {data[metric.key]}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} mb={4}>
        <Grid item size={{ xs: 12, md: 3, sm: 3 }}>
          <StatCard
            title="Total Revenue"
            value={`₹${parseFloat(data.totalStats?.total_revenue || 0).toLocaleString()}`}
            icon={<AttachMoney />}
            color="#66bb6a"
            tooltip="Total revenue generated in the selected period"
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 3, sm: 3 }}>
          <StatCard
            title="Avg. Invoice Value"
            value={`₹${parseFloat(data.totalStats?.average_invoice_value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
            icon={<TrendingUp />}
            color="#ffa726"
            tooltip="Average value per invoice"
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 3, sm: 3 }}>
          <StatCard
            title="Growth Rate"
            value={`${data.growthRate || 0}%`}
            icon={(data.growthRate || 0) >= 0 ? <TrendingUp /> : <TrendingDown />}
            color={(data.growthRate || 0) >= 0 ? "#66bb6a" : "#ef5350"}
            tooltip="Month-over-month growth rate"
            subtext={(data.growthRate || 0) >= 0 ? "Positive growth" : "Negative growth"}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 3, sm: 3 }}>
          <StatCard
            title="Total Invoices"
            value={(data.totalStats?.total_invoices || 0).toLocaleString()}
            icon={<Receipt />}
            color="#42a5f5"
            tooltip="Total number of invoices in the selected period"
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{mb: 4}}>
        <Grid item size={{ xs: 12, md: 4, sm: 8 }}>
          <Card sx={{ p: 2, borderRadius: 3, height: '100%' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" gutterBottom>
                ✨ Recent Invoices
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <input
                  type="date"
                  value={recentInvoicesDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={e => handleRecentInvoicesDateChange(e.target.value)}
                  placeholder="Filter by date"
                  style={{ 
                    padding: '6px 10px', 
                    borderRadius: 4, 
                    border: '1px solid #ccc', 
                    fontSize: 13,
                    width: '130px'
                  }}
                />
                {recentInvoicesDate && (
                  <Tooltip title="Reset to today">
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        const today = new Date().toISOString().slice(0, 10);
                        handleRecentInvoicesDateChange(today);
                      }}
                      sx={{ 
                        p: 0.5,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <Refresh fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box 
                sx={{ 
                  px: 2, 
                  py: 0.5, 
                  bgcolor: recentInvoicesDate ? 'info.main' : 'primary.main', 
                  borderRadius: 10, 
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                {recentInvoicesDate ? 'Total Amount' : 'Recent'} - ₹{(recentInvoicesDate ? filteredRecentInvoices : data?.recentInvoices)?.reduce((sum, invoice) => {
                  const amount = parseFloat(invoice.grand_total || 0);
                  return sum + (invoice.is_cn ? -amount : amount);
                }, 0).toLocaleString() || 0}
              </Box>
              {recentInvoicesDate && (
                <Box 
                  sx={{ 
                    px: 2, 
                    py: 0.5, 
                    bgcolor: 'warning.main', 
                    borderRadius: 10, 
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  {(recentInvoicesDate ? filteredRecentInvoices : data?.recentInvoices)?.filter(inv => !inv.is_cn).length || 0} invoices
                </Box>
              )}
            </Box>
            {renderRecentInvoices()}
          </Card>
        </Grid>
        <Grid item size={{ xs: 12, md: 4, sm: 4 }}>
          <Paper elevation={3} sx={{ p: 2, borderRadius: 3, height: '100%' }}>
            <Box mb={2}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6" fontWeight={500} display="flex" alignItems="center" gap={1}>
                📈 Recoveries
                <Tooltip title={showRecoveriesGraph ? "Show daily table" : "Show last 15 days graph"}>
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      setShowRecoveriesGraph(!showRecoveriesGraph);
                    }}
                    sx={{ ml: 0.5 }}
                  >
                    {showRecoveriesGraph ? <TableView fontSize="small" /> : <ShowChart fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refresh data">
                    <IconButton 
                      size="small" 
                      onClick={handleRefresh}
                      disabled={recoveriesRefreshing}
                      sx={{ ml: 0.5 }}
                    >
                      <Refresh 
                        fontSize="small" 
                        sx={{ 
                          animation: recoveriesRefreshing ? 'spin 1s linear infinite' : 'none',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          }
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Typography>
              </Box>
              {!showRecoveriesGraph && (
              <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                <Tooltip title="Previous day">
                  <IconButton 
                    size="small" 
                    onClick={() => navigateDate('prev')}
                    sx={{ 
                      p: 0.5,
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <NavigateBefore fontSize="small" />
                  </IconButton>
                </Tooltip>
                <input
                  type="date"
                  value={recoveriesDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={e => handleDateChange(e.target.value)}
                  style={{ 
                    padding: '4px 8px', 
                    borderRadius: 4, 
                    border: '1px solid #ccc', 
                    fontSize: 13,
                    width: '110px',
                    textAlign: 'center'
                  }}
                />
                <Tooltip title="Next day">
                  <IconButton 
                    size="small" 
                    onClick={() => navigateDate('next')}
                    disabled={recoveriesDate === new Date().toISOString().slice(0, 10)}
                    sx={{ 
                      p: 0.5,
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <NavigateNext fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              )}
              {showRecoveriesGraph && (
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <Tooltip title="Previous 15 days">
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        const current = new Date(recoveriesEndDate);
                        current.setDate(current.getDate() - 15);
                        const newEnd = current.toISOString().slice(0,10);
                        setRecoveriesEndDate(newEnd);
                        fetchRecoveriesLastDays(15, newEnd);
                      }}
                      sx={{ p: 0.5, '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <NavigateBefore fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {new Date(recoveriesStartDate).toLocaleDateString()} – {new Date(recoveriesEndDate).toLocaleDateString()}
                  </Box>
                  <Tooltip title="Next 15 days">
                    <span>
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          const todayStr = new Date().toISOString().slice(0,10);
                          const current = new Date(recoveriesEndDate);
                          current.setDate(current.getDate() + 15);
                          const newEnd = current.toISOString().slice(0,10);
                          const capped = newEnd > todayStr ? todayStr : newEnd;
                          setRecoveriesEndDate(capped);
                          fetchRecoveriesLastDays(15, capped);
                        }}
                        disabled={recoveriesEndDate >= new Date().toISOString().slice(0,10)}
                        sx={{ p: 0.5, '&:hover': { bgcolor: 'action.hover' } }}
                      >
                        <NavigateNext fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box 
                sx={{ 
                  px: 2, 
                  py: 0.5, 
                  bgcolor: 'primary.main', 
                  borderRadius: 10, 
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                {showRecoveriesGraph
                  ? `15 days - ₹${(recoveriesSeries.reduce((sum, p) => sum + Number(p.amount_received || 0), 0)).toLocaleString()}`
                  : `Received - ₹${todaysRecoveries.reduce((sum, customer) => sum + parseFloat(customer.total_amount_paid || 0), 0).toLocaleString()}`}
              </Box>
              <Box 
                sx={{ 
                  px: 2, 
                  py: 0.5, 
                  bgcolor: 'success.main', 
                  borderRadius: 10, 
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                {showRecoveriesGraph
                  ? `${recoveriesSeries.reduce((sum, p) => sum + Number(p.customers_paid || 0), 0)} customers`
                  : `${todaysRecoveries.length} customers`}
              </Box>
            </Box>
            {showRecoveriesGraph ? renderRecoveriesGraph() : renderTodaysRecoveries()}
          </Paper>
        </Grid>
        <Grid item size={{ xs: 12, md: 4, sm: 12 }}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={500} display="flex" alignItems="center" gap={1}>
              👥 Top Customers by Revenue
            </Typography>
            {renderTopCustomers()}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{mb: 4}}>
        
      </Grid>

      <Grid container spacing={3} mb={4}>
        <Grid item size={{ xs: 12, md: 8, sm: 8 }}>
          <ChartContainer 
            title="📈 Monthly Sales Performance"
            subtitle="Revenue trends and invoice counts over time"

            loading={chartsLoading}
          >
            <Box height={350} position="relative">
              <Line 
                data={salesAreaData} 
                options={{
                  ...createChartOptions('line'),
                  scales: {
                    y: {
                      position: 'left',
                      ticks: {
                        color: theme.palette.text.secondary,
                        callback: (value) => `₹${Number(value).toLocaleString()}`
                      },
                      grid: { color: theme.palette.divider, drawBorder: false },
                    },
                    y1: {
                      position: 'right',
                      ticks: {
                        color: theme.palette.text.secondary,
                        precision: 0,
                      },
                      grid: { display: false },
                    },
                    x: {
                      grid: { display: false },
                      ticks: { color: theme.palette.text.secondary }
                    }
                  },
                  plugins: {
                    ...createChartOptions('line').plugins,
                    tooltip: {
                      ...createChartOptions('line').plugins.tooltip,
                      callbacks: {
                        label: function(context) {
                          const label = context.dataset.label || '';
                          const parsed = context.parsed;
                          const value = (parsed && typeof parsed === 'object') ? (parsed.y ?? 0) : (parsed ?? 0);
                          if (label.includes('Sales')) {
                            return `${label}: ₹${Number(value).toLocaleString()}`;
                          }
                          return `${label}: ${Number(value).toLocaleString()}`;
                        },
                      },
                    },
                  },
                }}
              />
            </Box>
          </ChartContainer>
        </Grid>
        <Grid item size={{ xs: 12, md: 4, sm: 4 }}>
          <ChartContainer 
            title="📊 Invoice Distribution"
            subtitle="Breakdown by invoice type"
            loading={chartsLoading}
          >
            {renderInvoiceTypeDistribution()}
          </ChartContainer>
        </Grid>
      </Grid>

      <Grid container spacing={3} mb={4}>
        <Grid item size={{ xs: 12, md: 12, sm: 12 }} style={{marginTop: '45px'}}>
          <ChartContainer 
            title="📊 Monthly Invoice Count by Type"
            subtitle="GST and Kachcha invoice counts month by month"
            loading={chartsLoading}
          >
            {monthlyInvoiceTypeChartData.labels.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">No monthly invoice type data available</Typography>
              </Box>
            ) : (
              <Fade in={true} timeout={800}>
                <Box height={350} position="relative">
                  <Bar 
                    data={monthlyInvoiceTypeBarData} 
                    options={{
                      ...createChartOptions('bar'),
                      plugins: {
                        ...createChartOptions('bar').plugins,
                        tooltip: {
                          ...createChartOptions('bar').plugins.tooltip,
                          callbacks: {
                            label: function(context) {
                              const label = context.dataset.label || '';
                              const value = context.parsed.y ?? 0;
                              return `${label}: ${parseInt(value, 10)}`;
                            },
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </Fade>
            )}
          </ChartContainer>
        </Grid>
      </Grid>
    </Box>
  );
};


export default Analytics;